import { useState, useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ENDPOINTS, canonicalUrl, resolveHtmlAssetUrls, withTrailingSlash } from '../config';
import NotFound from './NotFound';

/**
 * One-way lookup aliases (legacy URL slug → current Policy Master slug).
 * These must NOT serve as dual canonicals — use LEGACY_POLICY_REDIRECTS so
 * alternate URLs 301/replace to the single preferred path.
 */
const POLICY_SLUG_ALIASES = {
    'privacy-policy': 'privacy-policy-2',
    'returns-refunds-policy': 'returns-and-refund-policy',
    'kcb_offers': 'kcb-offers',
};

/** Non-live paths that must move to the live WP URL. */
const LEGACY_POLICY_REDIRECTS = {
    'privacy-policy': '/privacy-policy-2/',
};

/**
 * Generic policy page — reads slug from URL params.
 * Mounted at /policy/:slug and at the site root (/:slug) so policy URLs
 * like /privacy-policy-2/, /delivery-policy/, /terms-and-conditions/ resolve.
 */
export default function PolicyPage() {
    const { slug } = useParams();

    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        // Legacy paths redirect via <Navigate>; skip fetch.
        if (!slug || LEGACY_POLICY_REDIRECTS[slug]) return;

        setPolicy(null);
        setLoading(true);
        setNotFound(false);

        const fetchBySlug = async (candidate) => {
            const res = await fetch(ENDPOINTS.POLICIES.BY_SLUG(candidate));
            const data = await res.json();
            return data.isOk && data.data ? data.data : null;
        };

        const fetchPolicy = async () => {
            try {
                let found = await fetchBySlug(slug);
                if (!found && POLICY_SLUG_ALIASES[slug]) {
                    found = await fetchBySlug(POLICY_SLUG_ALIASES[slug]);
                }
                if (found && found.isActive === false) {
                    found = null;
                }
                if (found) {
                    setPolicy(found);
                } else {
                    setNotFound(true);
                }
            } catch (err) {
                console.error('Error fetching policy:', err);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };

        fetchPolicy();
    }, [slug]);

    // /privacy-policy/ → live WP /privacy-policy-2/
    if (slug && LEGACY_POLICY_REDIRECTS[slug]) {
        return <Navigate to={LEGACY_POLICY_REDIRECTS[slug]} replace />;
    }

    /* ── Loading skeleton ── */
    if (loading) {
        return (
            <main className="pt-[10px] pb-16">
                <div className="max-w-[1440px] mx-auto px-4 animate-pulse">
                    <div className="h-8 bg-muted rounded w-1/3 mb-8"></div>
                    <div className="space-y-3">
                        {[...Array(10)].map((_, i) => (
                            <div
                                key={i}
                                className={`h-4 bg-muted rounded ${i % 3 === 2 ? 'w-2/3' : 'w-full'}`}
                            ></div>
                        ))}
                    </div>
                </div>
            </main>
        );
    }

    /* ── Not found — also reached for unknown root-level slugs, so render the
          real 404 page ── */
    if (notFound || !policy) {
        return <NotFound />;
    }

    /* ── Main content ── */
    // SEO meta — prefer the policy's configured meta fields, fall back to its title/description.
    const plainDescription = (policy.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const metaTitle = policy.metaTitle?.trim() || policy.title;
    const metaDescription = policy.metaDescription?.trim() || plainDescription.slice(0, 160);
    const metaKeywords = policy.metaKeywords?.trim() || '';

    return (
        <main className="pt-[10px] pb-16 bg-card">
            <Helmet>
                <title>{metaTitle}</title>
                <link rel="canonical" href={canonicalUrl(withTrailingSlash(`/${policy.slug}`))} />
                {metaDescription ? <meta name="description" content={metaDescription} /> : null}
                {metaKeywords ? <meta name="keywords" content={metaKeywords} /> : null}
            </Helmet>
            <div className="max-w-[1440px] mx-auto px-4">

                {/* Breadcrumb */}
                    {/* <nav className="mb-6 text-sm">
                        <ol className="flex items-center gap-2 text-muted-foreground">
                            <li>
                                <Link to="/" className="hover:text-primary transition-colors">
                                    Home
                                </Link>
                            </li>
                            <li><i className="ri-arrow-right-s-line"></i></li>
                            <li className="text-foreground font-medium">{policy.title}</li>
                        </ol>
                    </nav> */}

                {/* Scoped styles — mirrors the HTML stored in JoditEditor */}
                <style>{`
                    .policy-content {
                        --brand-navy: #1E2C4A;
                        --brand-orange: #E94326;
                        --text-dark: #2D3436;
                        --text-muted: #636E72;
                        line-height: 1.8;
                        color: var(--text-dark);
                    }
                    .dark .policy-content {
                        --brand-navy: oklch(var(--foreground));
                        --text-dark: oklch(var(--foreground));
                        --text-muted: oklch(var(--muted-foreground));
                    }
                    .policy-content h1 {
                        color: var(--brand-navy);
                        font-size: 2rem;
                        font-weight: 700;
                        margin: 1.5rem 0 0.75rem;
                    }
                    .policy-content h2 {
                        color: var(--brand-navy);
                        font-size: 1.1rem;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        border-bottom: 1px solid #eee;
                        padding-bottom: 12px;
                        margin-top: 0;
                        margin-bottom: 16px;
                    }
                    .dark .policy-content h2 {
                        border-bottom-color: oklch(var(--border));
                    }
                    .policy-content h3 {
                        color: var(--brand-navy);
                        font-size: 1rem;
                        font-weight: 600;
                        margin: 1rem 0 0.5rem;
                    }
                    .policy-content p {
                        margin-bottom: 16px;
                        color: var(--text-muted);
                    }
                    /* lists — NO default disc, use ::before orange bullet */

                    /* cards */
                    .policy-content .policy-card {
                        background: #fff;
                        border-radius: 12px;
                        padding: 40px;
                        box-shadow: 0 10px 30px rgba(0,0,0,0.05);
                        margin-bottom: 24px;
                        border-left: 5px solid transparent;
                        transition: transform 0.3s ease, border-color 0.3s ease;
                    }
                    .dark .policy-content .policy-card {
                        background: oklch(var(--card));
                        border: 1px solid oklch(var(--border));
                        border-left: 5px solid transparent;
                    }
                    .policy-content .policy-card:hover {
                        border-left-color: var(--brand-orange);
                        transform: translateX(5px);
                    }
                    /* contact section */
                    .policy-content .contact-section {
                        background-color: var(--brand-navy);
                        color: #fff;
                        border-radius: 12px;
                        padding: 40px;
                        margin-top: 40px;
                    }
                    .policy-content .contact-section h2 {
                        color: #fff;
                        border-bottom-color: rgba(255,255,255,0.1);
                    }
                    .policy-content .contact-section p {
                        color: rgba(255,255,255,0.8);
                    }
                    .policy-content .contact-section a {
                        color: var(--brand-orange);
                        text-decoration: none;
                        font-weight: 600;
                    }
                    .policy-content .contact-section a:hover { text-decoration: underline; }
                    .policy-content .address-box {
                        margin-top: 20px;
                        padding-top: 20px;
                        border-top: 1px solid rgba(255,255,255,0.1);
                    }
                    /* misc */
                    .policy-content strong { color: var(--brand-navy); font-weight: 600; }
                    .policy-content a { color: var(--brand-orange); text-decoration: none; }
                    .policy-content a:hover { text-decoration: underline; }
                    .policy-content img { max-width: 100%; border-radius: 8px; margin: 12px 0; }
                    .policy-content table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
                    .policy-content th { border: 1px solid #D1D5DB; background: #F3F4F6; padding: 8px; text-align: left; }
                    .policy-content td { border: 1px solid #D1D5DB; padding: 8px; }
                    .policy-content .orange-divider {
                        width: 80px; height: 4px;
                        background-color: var(--brand-orange);
                        margin: 8px auto 24px;
                        border-radius: 2px;
                    }
                `}</style>

                {/* Rich HTML from JoditEditor */}
                <div
                    className="policy-content leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: resolveHtmlAssetUrls(policy.description) }}
                />

                {/* Footer note */}
                <div className="mt-12 pt-6 border-t border-border text-sm text-muted-foreground text-center">
                    <p>
                        If you have any questions, please{' '}
                        <Link to="/contact/" className="text-primary hover:underline">
                            contact us
                        </Link>
                        .
                    </p>
                </div>
            </div>
        </main>
    );
}
